import classNames from 'classnames'
import { z } from 'zod'
import * as styles from './segments-bar.css'

type SegmentVariant = 'solid' | 'dashed'

export type SegmentProps<TLabel extends string = string> = {
  ariaLabel: TLabel
  percentage: number
  color: string
  className?: string
  variant?: SegmentVariant
  pressed?: boolean
  dimmed?: boolean
  onClick?: () => unknown
}

export const SegmentsBarSizeSchema = z.enum(['small', 'tiny'])
export type SegmentsBarSize = z.infer<typeof SegmentsBarSizeSchema>

type SegmentsBarProps = React.ComponentPropsWithRef<'div'> & {
  segments: SegmentProps[]
  size?: SegmentsBarSize
}

export const SegmentsBar: React.FC<SegmentsBarProps> = ({ segments, size = 'small', ref, className, ...rest }) => {
  return (
    <div ref={ref} className={classNames(styles.bar, styles.barSize[size], className)} {...rest}>
      {segments.map((segment, index) => (
        <Segment key={segment.ariaLabel} {...segment} isFirst={index === 0} isLast={index === segments.length - 1} />
      ))}
    </div>
  )
}

const Segment: React.FC<SegmentProps & { isFirst: boolean; isLast: boolean }> = ({ isFirst, isLast, ...segment }) => {
  // Render 0% segments as a 1% sliver so the category stays visible in the bar
  const width = Math.max(segment.percentage, 1)
  const isDashed = segment.variant === 'dashed'
  const segmentClass = classNames(
    styles.segment({
      variant: segment.variant,
      interactive: !!segment.onClick || undefined,
      pressed: !!segment.pressed || undefined,
      dimmed: !!segment.dimmed || undefined,
    }),
    segment.className
  )

  const style: React.CSSProperties = {
    width: `${width}%`,
    backgroundColor: isDashed ? undefined : segment.color,
    borderTopLeftRadius: isFirst ? '8px' : undefined,
    borderBottomLeftRadius: isFirst ? '8px' : undefined,
    borderTopRightRadius: isLast ? '8px' : undefined,
    borderBottomRightRadius: isLast ? '8px' : undefined,
  }

  return segment.onClick ? (
    <button
      aria-label={segment.ariaLabel}
      aria-pressed={segment.pressed}
      className={segmentClass}
      style={style}
      onClick={segment.onClick}
    />
  ) : (
    <div aria-label={segment.ariaLabel} aria-pressed={segment.pressed} className={segmentClass} style={style} />
  )
}
